import { Box, Button, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  generatePath,
  useNavigate,
  useParams,
  useMatch,
  Navigate,
} from "react-router-dom";
import { getVirtualContractDefaultFormValues } from "src/entities/partnerContracts/model/constants/formValues";
import { useGetPartnerQuery } from "src/shared/api/partner";
import {
  EConfirmModalType,
  Loader,
  LoadingButton,
  ModalComponent,
} from "src/shared/components";
import { ContractBody } from "src/shared/components/contracts/ContractBody";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { EContractState } from "src/shared/types/partner.types";
import {
  useGetpartnerComplexContractsListQuery,
  useGetPartnerVirtualContractBonusRangesQuery,
  useGetPartnerVirtualContractQuery,
  useLinkWithComplexContractMutation,
  usePutPartnerVirtualContractMutation,
  useUnlinkWithComplexContractMutation,
} from "../../../model/api/api";
import { getPartnerVirtualContractFormValues } from "../../../model/helpers/helpers";
import { IPartnerVirtualContractFormValues } from "../../../model/types";
import { VirtualContractForm } from "./VirtualContractForm";

export function EditPartnerVirtualContract() {
  const navigate = useNavigate();
  const isIframeRoute = useMatch("/iframe/*");

  const { partner_id, contract_id } = useParams();
  const goBackRoute = partner_id
    ? generatePath(
        isIframeRoute
          ? routes.iframePartnerContracts
          : routes.partnerContractsList,
        { partner_id },
      )
    : "/404";

  const { data: partner } = useGetPartnerQuery(partner_id || skipToken);
  const methods = useForm<IPartnerVirtualContractFormValues>({
    mode: "onChange",
    defaultValues: getVirtualContractDefaultFormValues({
      partner_id: partner_id || 0,
    }),
  });
  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = methods;

  const requestData =
    partner_id && contract_id
      ? {
          partner_id,
          contract_id,
        }
      : skipToken;
  const [putContract, { isLoading: isLoadingPut }] =
    usePutPartnerVirtualContractMutation();

  // checking if form is submitting we prevend extra requets
  // when contract is updated and tags are invalidated but modal
  // is not closed yet
  const { data: bonus_ranges } = useGetPartnerVirtualContractBonusRangesQuery(
    isSubmitting ? skipToken : requestData,
  );
  const {
    data: contract,
    isLoading: isLoadingContract,
    error: contractError,
  } = useGetPartnerVirtualContractQuery(isSubmitting ? skipToken : requestData);
  const { data: complexContracts } = useGetpartnerComplexContractsListQuery(
    partner_id
      ? {
          partner_id,
        }
      : skipToken,
  );
  const [linkWithComplexContract, { isLoading: isLoadingLink }] =
    useLinkWithComplexContractMutation();
  const [unlinkWithComplexContract, { isLoading: isLoadingUnlink }] =
    useUnlinkWithComplexContractMutation();

  const isLoading = isLoadingPut || isLoadingLink || isLoadingUnlink;
  const complexContractsOptions = useMemo(
    () =>
      complexContracts?.data.map(({ id, contract_number, contract_state }) => ({
        name:
          contract_number +
          (contract_state.value === EContractState._5 ? " (закрыт)" : ""),
        value: id,
        disabled: contract_state.value === EContractState._5,
      })),
    [complexContracts],
  );

  const bonusRangesToShow = bonus_ranges?.partner_bonuses_percents_grid.length
    ? bonus_ranges?.partner_bonuses_percents_grid
    : bonus_ranges?.default_bonuses_percents_grid;

  const handleSaveContract = async (
    data: IPartnerVirtualContractFormValues,
  ) => {
    const shouldLinkWithKDA =
      data.partner_contract_id &&
      data.partner_contract_id !== contract?.partner_contract_id;
    const shouldUnLinkWithKDA =
      !data.partner_contract_id && contract?.partner_contract_id;
    const shouldUpdateContract = Object.keys(dirtyFields).some(
      (key) => key !== "partner_contract_id",
    );
    try {
      // here to avoid unnecessary refetching data we wrap requests in Primice.all
      const requestsArray = [];
      if (shouldUpdateContract) {
        requestsArray.push(
          putContract({
            id: Number(contract_id),
            ...data,
            contract_date: data.contract_date.format("YYYY-MM-DD"),
          }).unwrap(),
        );
      }
      if (shouldLinkWithKDA) {
        requestsArray.push(
          linkWithComplexContract({
            complex_contract_id: Number(data.partner_contract_id),
            virtual_contract_id: Number(contract_id),
            partner_id: Number(partner_id),
          }).unwrap(),
        );
      }
      if (shouldUnLinkWithKDA && contract) {
        requestsArray.push(
          unlinkWithComplexContract({
            complex_contract_id: Number(contract.partner_contract_id),
            virtual_contract_id: Number(contract_id),
            partner_id: Number(partner_id),
          }).unwrap(),
        );
      }
      await Promise.all(requestsArray);
      navigate(goBackRoute);
    } catch (e) {
      showApiError(e);
    }
  };

  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(goBackRoute),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });
  useEffect(() => {
    if (contract) {
      reset(getPartnerVirtualContractFormValues(contract));
    }
  }, [contract, reset]);

  if (!partner_id || !contract_id) {
    return <Navigate to="/404" replace />;
  }

  if (
    contractError &&
    "status" in contractError &&
    contractError.status === 404
  ) {
    return <Navigate to="/404" replace />;
  }

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      boxProps={{ sx: { height: "90vh" } }}
      disableBodyPadding
      initialBodyShadow
      title={
        <Typography>
          <Typography component={"span"} color="primary">
            {contract?.contract_number}
          </Typography>
          <Typography component={"span"} color="textSecondary">
            &nbsp;({partner?.name})
          </Typography>
        </Typography>
      }
      onClose={isDirty ? showModal : () => navigate(goBackRoute)}
      open
      modalFooter={
        <Box
          sx={{
            display: "flex",
            gap: 2,
            ml: "auto",
            justifyContent: "end",
          }}
        >
          <LoadingButton
            isLoading={isLoading}
            variant="contained"
            onClick={handleSubmit(handleSaveContract)}
            disabled={isLoading}
          >
            Сохранить изменения
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={isDirty ? showModal : () => navigate(goBackRoute)}
          >
            Закрыть
          </Button>
        </Box>
      }
    >
      {isLoadingContract && <Loader position="absolute" blur />}
      {modal}
      <FormProvider {...methods}>
        <ContractBody
          text="Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
                "
          form={
            <VirtualContractForm
              partnerId={partner_id}
              isEdit
              bonusRanges={bonusRangesToShow}
              currency={contract?.service_currency.value}
              complexContractsOptions={complexContractsOptions || []}
              KDAcurrency={bonus_ranges?.contract?.currency.value}
            />
          }
        />
      </FormProvider>
    </ModalComponent>
  );
}
